/* eslint-disable @typescript-eslint/no-explicit-any */

import { CacheBasicConfig, CacheConfigMultiKey, CacheMultiKeyConfig, types } from './interfaces';

type ObjectMap = Map<string, unknown>;

export class CacheUtils {
  static isFunction(attr: unknown): attr is types.Func {
    return typeof attr === 'function';
  }

  static isString(attr: unknown): attr is string {
    return typeof attr === 'string';
  }

  static isCacheMultiKeyConfig<T>(config: unknown): config is CacheMultiKeyConfig<T> {
    return (config as CacheMultiKeyConfig).multiKey !== undefined;
  }

  static isCacheBasicConfig<T>(config: unknown): config is CacheBasicConfig<T> {
    return !CacheUtils.isCacheMultiKeyConfig(config);
  }

  static defaultParamKeyMapper(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  static generateObjectMap(args: any[], keys?: string[]): ObjectMap {
    const k = new Map<string, unknown>();
    const lookupKeys = keys?.length ? keys : args.map((_, index) => `$${index}`);
    lookupKeys.forEach((key: string) => {
      let v: unknown = null;
      const indexMatch = key.match(/^\$(\d+)$/);
      if (indexMatch) {
        // by param index
        const index = parseInt(indexMatch[1]);
        v = args[index];
      } else {
        // by property name
        args.find((arg) => {
          if (arg === null || arg === undefined) {
            return false;
          }
          if (arg instanceof Object && Object.hasOwn(arg, key)) {
            v = arg[key];
            return true;
          }
          return false;
        });
      }
      if (v === null || v === undefined || CacheUtils.isFunction(v)) {
        v = null;
      }
      k.set(key, v);
    });
    return k;
  }

  static generateSimpleKey(args: any[], keys?: string[]): string {
    const map = CacheUtils.generateObjectMap(args, keys);
    const k = Object.fromEntries(map);
    const result = JSON.stringify(k);
    return result;
  }

  static generateMultiKeys(args: any[], cfg: CacheConfigMultiKey): unknown[] {
    const value = CacheUtils.isString(cfg.paramProperty)
      ? args[cfg.paramIndex][cfg.paramProperty]
      : args[cfg.paramIndex];
    if (value === null || value === undefined || !Array.isArray(value)) {
      throw new Error(`Invalid cache key mapping. Expected an array, got ${typeof value}`);
    }
    return value;
  }

  static replaceMultiKeys(args: any[], cfg: CacheConfigMultiKey, newKeys: any): any[] {
    const value = [...args];
    if (CacheUtils.isString(cfg.paramProperty)) {
      value[cfg.paramIndex][cfg.paramProperty] = newKeys;
    } else {
      value[cfg.paramIndex] = newKeys;
    }
    return value;
  }
}
